import type { KeyboardEvent } from 'react';
import type { IframePoolItem } from '../../../modules/api';
import { formatPrice } from '../../../modules/currency';
import { Image } from '../../../modules/design-system';
import { TILE_CLASSES } from './tile';

type Props = {
  item: IframePoolItem;
  onSelect: (item: IframePoolItem) => void;
};

/**
 * One "What's inside" tile — a dark card with the whole graded slab 20px in
 * from its edges and the price on its own line under it (partner Figma
 * 2026-10-14: show the full card, price below; it used to run to the tile's
 * bottom edge and fade under the price, which cropped the slab).
 */
export function WhatsInsideItem({ item, onSelect }: Props) {
  const title = item.title || "What's inside";
  const priceLabel = formatPrice(item.itemPrice, 2);
  const select = () => onSelect(item);
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    select();
  };

  return (
    <article
      data-co-component="WhatsInsideItem"
      role="button"
      tabIndex={0}
      aria-label={title}
      onClick={select}
      onKeyDown={onKeyDown}
      className={
        TILE_CLASSES +
        ' flex flex-col gap-3 p-5 cursor-pointer transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]'
      }
    >
      {/* `rounded-none`: Image's wrapper inherits its parent's radius and
          clips to it — the tile's 18px would shave the slab's corners. */}
      <div className="relative min-h-0 flex-1 rounded-none">
        <Image
          src={item.imageUrl}
          alt={title}
          className="absolute inset-0"
          imgClassName="absolute inset-0 h-full w-full object-contain"
          width={195}
          height={287}
          quality={95}
        />
      </div>
      {priceLabel && (
        <span className="truncate text-center text-[14px] leading-[20px] font-semibold tracking-[-0.15px] text-[var(--co-WhatsInsideItem-priceColor,#FFFFFF)]">
          ~{priceLabel}
        </span>
      )}
    </article>
  );
}
